'use client';
import BlockHeader from '@/components/UIkit/BlockHeader';
import Image from 'next/image';
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { Navigation, Autoplay } from 'swiper/modules';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Autoplay]);

export interface SliderBlockV2Props {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  formatList: Array<{
    title: ReactNode | SerializedEditorState;
    txtContent: ReactNode | SerializedEditorState;
    bgImage?: string | { url: string; alt: string };
  }>;
  color?: 'violet' | 'blue' | 'green';
}

export default function SliderBlockV2({
  title,
  formatList,
  id,
  subtitle,
  color = 'blue',
}: SliderBlockV2Props) {
  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn-h',
      blue: 'custom-gradient-blue-btn-h',
      violet: 'custom-gradient-violet-btn-h',
    }[color] || 'custom-gradient-green';
  const backgroundProggressColorClass =
    {
      green: 'custom-gradient-green',
      blue: 'custom-gradient-blue',
      violet: 'custom-gradient-violet',
    }[color] || 'custom-gradient-green';
  const swiperRef = useRef<SwiperCore | null>(null);
  const [progress, setProgress] = useState(0);
  const [activeSlide, setActiveSlide] = useState<number | null>(null);

  // Function to calculate progress percentage
  const calculateProgress = (swiper: SwiperCore) => {
    const prevView = () => {
      if (typeof swiper.params?.slidesPerView === 'number') {
        return swiper.params.slidesPerView;
      } else {
        return 1; // Default to 1 if slidesPerView is not a number
      }
    };

    if (swiper.slides && swiper.slides.length > 0) {
      const progress = (swiper.activeIndex / (swiper.slides.length - prevView())) * 100;
      const currentProgress = progress || 25;
      setProgress(currentProgress);
    }
  };

  const handlePrevClick = () => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  };

  const handleNextClick = () => {
    if (swiperRef.current) swiperRef.current.slideNext();
  };

  const handlePlusClick = (index: number) => {
    if (swiperRef.current) {
      if (activeSlide === index) {
        // Close content and resume autoplay
        swiperRef.current.autoplay.start();
        setActiveSlide(null);
      } else {
        // Open content and stop autoplay
        swiperRef.current.autoplay.stop();
        setActiveSlide(index);
      }
    }
  };

  useEffect(() => {
    if (swiperRef.current) {
      // Initialize the progress bar when the component first loads
      calculateProgress(swiperRef.current);
    }
  }, []);

  return (
    <div id={id} data-meta="SliderBlockV2" className="container mx-auto px-4 sm:px-6 lg:px-8 my-24">
      <BlockHeader title={title} subtitle={subtitle} />
      <div className="flex justify-center flex-col gap-4">
        <div className="relative overflow-hidden">
          <Swiper
            onSwiper={swiper => {
              swiperRef.current = swiper;
              // Initialize the progress when swiper is mounted
              calculateProgress(swiper);
            }}
            onSlideChange={() => {
              if (swiperRef.current) {
                // Update progress on slide change
                calculateProgress(swiperRef.current);
              }
            }}
            spaceBetween={1}
            slidesPerView={1}
            breakpoints={{
              640: {
                slidesPerView: 1,
              },
              1024: {
                slidesPerView: 2,
              },
              1280: {
                slidesPerView: 3,
              },
            }}
          >
            {formatList.map((item, index) => (
              <SwiperSlide key={index}>
                <div className="relative p-4">
                  <Image
                    alt={typeof item.bgImage === 'string' ? '' : item.bgImage?.alt || ''}
                    src={
                      typeof item.bgImage === 'string'
                        ? item.bgImage
                        : item.bgImage?.url || '/assets/photoHero/sliderBGStazhuvannia/bg1.webp'
                    }
                    width={500}
                    height={500}
                    layout="responsive"
                    className=""
                  />
                  <div
                    className={`absolute ${
                      activeSlide === index ? 'top-4' : 'bottom-4'
                    } left-4 right-4 flex flex-col p-4 transition-all duration-300`}
                  >
                    <div
                      className={`p-6 ${
                        activeSlide === index ? 'border-b border-white' : ''
                      } mb-2 flex gap-2 justify-between items-center`}
                    >
                      {item.title && (
                        <>
                          <CustomRichText
                            data={item.title}
                            tag="div"
                            className="text-xl font-bold text-white overflow-hidden"
                          />
                          <div
                            className="border border-white rounded-full cursor-pointer"
                            onClick={() => handlePlusClick(index)}
                          >
                            {activeSlide === index ? (
                              <XMarkIcon aria-hidden="true" className="text-white h-8 w-8" />
                            ) : (
                              <PlusIcon aria-hidden="true" className="text-white h-8 w-8" />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <CustomRichText
                      data={item.txtContent}
                      tag="div"
                      className={`text-sm text-white transition-opacity duration-300 ${
                        activeSlide === index ? 'block opacity-100' : 'hidden opacity-0'
                      }`}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="flex justify-center items-center gap-4 w-full xl:hidden">
          <div className="flex gap-x-2">
            <button
              onClick={handlePrevClick}
              className={`p-2 border rounded-full text-gray-400 hover:border-white hover:text-white ${backgroundColorClass}`}
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={handleNextClick}
              className={`p-2 border rounded-full text-gray-400 hover:border-white hover:text-white ${backgroundColorClass}`}
            >
              <ArrowRightIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="h-1 w-full bg-neutral-200 rounded-3xl">
            <div
              className={`h-1 ${backgroundProggressColorClass} rounded-3xl`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}
